import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import {
  hasOwnKey,
  toNullableStr,
  toNumberOr,
  toNumberOrNull,
  toSnakeCaseChangesBody,
  toStr,
  unwrapApiData,
} from "./points-service-helpers";
import {
  CreatePointRewardPayload,
  DEFAULT_QR_EXPIRATION_HOURS,
  MAX_QR_EXPIRATION_HOURS,
  MIN_QR_EXPIRATION_HOURS,
  POINT_REWARD_TYPE_LABELS,
  PointReward,
  PointRewardChanges,
  PointRewardEditableField,
  PointRewardType,
  UpdatePointRewardPayload,
} from "../../models/pointReward";

/** camelCase de PointRewardChanges → snake_case del body. También es la lista blanca de campos que viajan. */
const POINT_REWARD_CHANGE_BODY_KEYS: Record<PointRewardEditableField, string> = {
  title: "title",
  description: "description",
  pointsCost: "points_cost",
  rewardType: "reward_type",
  stock: "stock",
  imageUrl: "image_url",
  partnerId: "partner_id",
  partnerLocationInfo: "partner_location_info",
  qrExpirationHours: "qr_expiration_hours",
  active: "active",
};

const pointRewardTag = (id: number) => ({ type: "pointRewards" as const, id });
const pointRewardListTag = { type: "pointRewards" as const, id: "LIST" };

const isPointRewardType = (value: unknown): value is PointRewardType =>
  hasOwnKey(POINT_REWARD_TYPE_LABELS, value);

const isQrExpirationHours = (value: number | null): value is number =>
  value !== null && Number.isInteger(value) && value >= MIN_QR_EXPIRATION_HOURS && value <= MAX_QR_EXPIRATION_HOURS;

// Fuera de contrato (no entero o fuera de 1..720) no hay un valor correcto que mostrar: se usa el default.
const toQrExpirationHours = (value: unknown): number => {
  const hours = toNumberOrNull(value);
  return isQrExpirationHours(hours) ? hours : DEFAULT_QR_EXPIRATION_HOURS;
};

const DEFAULT_REWARD_TYPE: PointRewardType = "discount_code";

export const mapPointReward = (raw: unknown): PointReward => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  return {
    id: toNumberOr(row.id, 0),
    tenantId: toStr(row.tenant_id),
    title: toStr(row.title),
    description: toNullableStr(row.description),
    pointsCost: toNumberOr(row.points_cost, 0),
    rewardType: isPointRewardType(row.reward_type) ? row.reward_type : DEFAULT_REWARD_TYPE,
    // 0 es un valor válido (agotado): solo null o no numérico significa ilimitado.
    stock: toNumberOrNull(row.stock),
    imageUrl: toNullableStr(row.image_url),
    partnerId: toNullableStr(row.partner_id),
    partnerName: toNullableStr(row.partner_name),
    partnerLocationInfo: toNullableStr(row.partner_location_info),
    qrExpirationHours: toQrExpirationHours(row.qr_expiration_hours),
    active: row.active === true,
    createdAt: toStr(row.created_at),
    updatedAt: toStr(row.updated_at),
    pendingRedemptionCount: Math.max(0, toNumberOr(row.pending_redemption_count, 0)),
  };
};

/**
 * Body snake_case de POST/PUT: solo los campos de la lista blanca que tienen valor
 * (null sí viaja, undefined no). Nunca salen tenant_id ni id.
 */
export const toPointRewardChangesBody = (changes: PointRewardChanges): Record<string, unknown> =>
  toSnakeCaseChangesBody(changes, POINT_REWARD_CHANGE_BODY_KEYS);

export const pointRewardsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPointRewards: build.query<PointReward[], string>({
      query: (tenantId) => ({
        url: API_ENDPOINTS.POINTS.REWARDS,
        // Siempre con inactivos: el BO los necesita para reactivarlos y para contar los activos.
        params: { tenant_id: tenantId, include_inactive: "true" },
      }),
      transformResponse: (response: unknown): PointReward[] => {
        const rows = unwrapApiData(response);
        return Array.isArray(rows) ? rows.map((row: unknown) => mapPointReward(row)) : [];
      },
      providesTags: (result) => [
        ...(result ?? []).map(({ id }) => pointRewardTag(id)),
        pointRewardListTag,
      ],
    }),

    createPointReward: build.mutation<PointReward, CreatePointRewardPayload>({
      query: ({ tenantId, ...changes }) => ({
        url: API_ENDPOINTS.POINTS.REWARDS,
        method: "POST",
        body: { tenant_id: tenantId, ...toPointRewardChangesBody(changes) },
      }),
      transformResponse: (response: unknown) => mapPointReward(unwrapApiData(response)),
      invalidatesTags: [pointRewardListTag],
    }),

    updatePointReward: build.mutation<PointReward, UpdatePointRewardPayload>({
      query: ({ id, changes }) => ({
        url: API_ENDPOINTS.POINTS.REWARD_BY_ID(id),
        method: "PUT",
        body: toPointRewardChangesBody(changes),
      }),
      transformResponse: (response: unknown) => mapPointReward(unwrapApiData(response)),
      invalidatesTags: (_result, _error, arg) => [pointRewardTag(arg.id), pointRewardListTag],
    }),

    deactivatePointReward: build.mutation<PointReward, number>({
      query: (id) => ({
        url: API_ENDPOINTS.POINTS.REWARD_BY_ID(id),
        method: "DELETE",
      }),
      transformResponse: (response: unknown) => mapPointReward(unwrapApiData(response)),
      invalidatesTags: (_result, _error, id) => [pointRewardTag(id), pointRewardListTag],
    }),
  }),
});

export const {
  useGetPointRewardsQuery,
  useCreatePointRewardMutation,
  useUpdatePointRewardMutation,
  useDeactivatePointRewardMutation,
} = pointRewardsApi;
